import React from "react";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import Link from "components/link";
import IconButton from "components/icon-button";
import { FormattedMessage } from "react-intl";
import Icon from "components/icon";
import { useMutation } from "@apollo/react-hooks";
import { DeleteActionWithModal } from "components/buttons";
import { useCurrentCustomerContext } from "components/current-customer-context";
import { ConnectedPaginatedTable, TableIconCell, CustomTableRow } from "components/table";
import assetQuery from "./query";
import commonAssetQuery from "./query-common";
import styles from "./styles.css.js";
import mutation from "./mutation";
import withStyles from "components/with-styles";
import PropTypes from "prop-types";
import { Restricted, permissions, useAuthDataContext } from "components/auth";
import { AssetDisplay } from "components/thumbnail";
import { injectIntl } from "react-intl";
import { HeaderCell } from "components/table";
import { DataGridItem } from "components/data-grid";
import Chip from "components/chip";

const Header = ({ onChangeOrdering, orderBy }) => (
  <TableRow>
    <HeaderCell />
    <HeaderCell onChangeOrdering={onChangeOrdering} orderBy={orderBy} orderName="orderByCodename">
      <FormattedMessage id="common.name" />
    </HeaderCell>
    <TableIconCell />
  </TableRow>
);

Header.propTypes = {
  /**
   ** Callback que se invoca al cambiar la columna actual
   **/
  onChangeOrdering: PropTypes.func,
  /**
   ** Ordenación de la columna actual
   **/
  orderBy: PropTypes.object,
};

const RowComponent = ({ classes, intl, item, commonAssets, refetchData }) => {
  const [deleteAsset] = useMutation(mutation);
  const { currentCustomerId } = useCurrentCustomerContext();

  const detailsUrl = commonAssets
    ? `/asset/details/${item.id}`
    : `/asset/details/${item.id}/customer/${currentCustomerId}`;
  const hasGraphicsets = item && item.hasGraphicsets;

  return (
    <CustomTableRow key={item.id} href={detailsUrl}>
      <TableCell>
        <AssetDisplay preview asset={item} width={50} />
      </TableCell>
      <TableCell>
        <div className={classes.title}>{item.codename}</div>

        <div>{item.format}</div>
        {item.format !== "mp4" && (
          <div>
            {item.sizePx.x}x{item.sizePx.y}
          </div>
        )}
        <div>
          {item.categories && item.categories.length > 0 && (
            <DataGridItem big>
              <div className={classes.keywordContainer}>
                {item.categories.map(category => (
                  <Chip className={classes.category} key={category.name} label={category.name} />
                ))}
              </div>
            </DataGridItem>
          )}
        </div>
      </TableCell>
      <TableIconCell>
        <Link href={detailsUrl} >
          <IconButton className={classes.iconButton}>
            <Icon name="launch" />
          </IconButton>
        </Link>
        <Restricted permissions={[commonAssets ? permissions.asset_common_manage : permissions.asset_manage]}>
          <Link href={`/asset/edit/${item.id}`}>
            <IconButton className={classes.iconButton}>
              <Icon name="edit" />
            </IconButton>
          </Link>
          <DeleteActionWithModal
            disabledTooltipText={intl.formatMessage({ id: "app.asset.delete.hasGraphicsets" })}
            disabled={hasGraphicsets}
            mutation={deleteAsset}
            mutationVariables={{
              assetId: item.id,
              customerId: currentCustomerId,
            }}
            elementNameInResult="deleteAsset"
            elementToDelete={item.codename}
            refetchDataOnSuccess
            refetchData={refetchData}
          />
        </Restricted>
      </TableIconCell>
    </CustomTableRow>
  );
};

RowComponent.propTypes = {
  /**
   ** Elemento a representar en la fila de la tabla
   **/
  item: PropTypes.object,
  /**
   ** Indica si se trata de una tabla de assets comunes o no
   **/
  commonAssets: PropTypes.bool,
  /**
   * Función de refetch de datos
   */
  refetchData: PropTypes.func.isRequired,
};

const I18nStyledRowComponent = injectIntl(withStyles(styles)(RowComponent));

const AssetPaginatedTable = ({ commonAssets = false, debounce, ...props }) => {
  const { hasAdminPrivileges } = useAuthDataContext();
  return (
    <ConnectedPaginatedTable
      gqlQuery={commonAssets ? commonAssetQuery : assetQuery}
      manuallyScopedQuery={!commonAssets && hasAdminPrivileges}
      headerComponent={Header}
      rowComponent={I18nStyledRowComponent}
      rowProps={{ commonAssets }}
      paginatedElementsHandler={data => (commonAssets ? data.commonAssets : data.assets)}
      defaultOrdering={{ orderByCodename: "ASC" }}
      {...props}
    />
  );
};

AssetPaginatedTable.propTypes = {
  /**
   ** Página a cargar en la tabla
   **/
  page: PropTypes.number,
  /**
   * Indica si la query se debouncea.
   **/
  debounce: PropTypes.bool,
  /**
   ** Indica si se recuperan los assets sin customer o no
   **/
  commonAssets: PropTypes.bool,
};

export default AssetPaginatedTable;
